import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Building2, ArrowUp, ArrowDown, Clock, TrendingUp, TrendingDown,
  Network, Loader2, RefreshCw, BarChart3, Users, AlertTriangle, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last 12 months' },
];

const SORT_OPTIONS = [
  { value: 'total', label: 'Total referrals' },
  { value: 'converted', label: 'Conversions' },
  { value: 'conversion_rate', label: 'Conversion rate' },
  { value: 'match_rate', label: 'Match rate' },
  { value: 'time_to_match', label: 'Time-to-match (fastest first)' },
];

const ADMIN_NAV = [
  { icon: BarChart3, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Network, label: 'Referral Sources', active: true },
  { icon: Building2, label: 'Multi-location', href: '/provider/multi-location' },
];

const fmtHours = (h) => {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const RateCell = ({ value, target = 50 }) => {
  const tone = value >= target ? 'emerald' : value >= target * 0.6 ? 'amber' : 'red';
  const cls = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  }[tone];
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${cls}`}>{value.toFixed(1)}%</span>;
};

export default function AdminReferralSources() {
  const { getAuthHeader } = useAuth();
  const [window, setWindow] = useState(90);
  const [sortBy, setSortBy] = useState('total');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingQbr, setDownloadingQbr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/operations/referral-sources`, {
        ...getAuthHeader(),
        params: { window_days: window, sort_by: sortBy },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load scorecard');
    } finally {
      setLoading(false);
    }
  }, [window, sortBy, getAuthHeader]);

  const downloadQbr = useCallback(async (organization) => {
    const slug = organization.replace(/[^a-z0-9]/gi, '_').toLowerCase().replace(/^_+|_+$/g, '');
    setDownloadingQbr(slug);
    try {
      const headers = getAuthHeader().headers || {};
      const resp = await axios.get(
        `${API}/operations/referral-sources/${slug}/qbr.pdf`,
        { headers, responseType: 'blob', params: { window_days: window } },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom_qbr_${slug}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`QBR packet for ${organization} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build the QBR PDF');
    } finally {
      setDownloadingQbr(null);
    }
  }, [window, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || { total: 0, matched: 0, converted: 0, conversion_rate: 0, match_rate: 0, distinct_sources: 0 };

  // Compute insight signals from sources
  const insights = useMemo(() => {
    if (!data?.sources?.length) return null;
    const sorted = [...data.sources];
    const topConverter = sorted.slice().sort((a, b) => b.converted - a.converted)[0];
    const fastestMatch = sorted.filter((s) => s.avg_time_to_match_hours !== null)
      .sort((a, b) => a.avg_time_to_match_hours - b.avg_time_to_match_hours)[0];
    const coldest = sorted.filter((s) => s.match_rate < 30 && s.total >= 3)[0];
    return { topConverter, fastestMatch, coldest };
  }, [data]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Referral Source Scorecard"
      subtitle="Which hospitals and partners send referrals that actually convert. Use it to prioritize BD calls, negotiate VBC contracts, and coach your intake team."
      testId="admin-referral-sources-page"
    >
      {/* CONTROL STRIP */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="ref-sources-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Window</span>
          <select
            value={window}
            onChange={(e) => setWindow(Number(e.target.value))}
            className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white"
            data-testid="ref-sources-window"
          >
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Sort by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white"
            data-testid="ref-sources-sort"
          >
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="ml-auto">
          <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="ref-sources-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI STRIP */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="ref-sources-kpis">
        <KpiTile label="Referrals" value={totals.total} icon={Network} sub={`${totals.distinct_sources} distinct sources`} testid="ref-kpi-total" />
        <KpiTile label="Matched" value={totals.matched} icon={Users} sub={<RateCell value={totals.match_rate} target={70} />} testid="ref-kpi-matched" />
        <KpiTile label="Converted to episodes" value={totals.converted} icon={TrendingUp} sub={<RateCell value={totals.conversion_rate} target={45} />} testid="ref-kpi-converted" />
        <KpiTile label="Sources" value={totals.distinct_sources} icon={Building2} sub={`${data?.window_days || 0}-day window`} testid="ref-kpi-sources" />
      </div>

      {/* INSIGHT CARDS */}
      {insights && (
        <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="ref-sources-insights">
          {insights.topConverter && (
            <div className="cn-card p-5" data-testid="ref-insight-top-converter">
              <div className="flex items-center gap-2 text-emerald-700 mb-2">
                <ArrowUp className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-wider font-bold">Top converter</span>
              </div>
              <p className="font-semibold text-cn-text">{insights.topConverter.organization}</p>
              <p className="text-xs text-cn-text-muted mt-1">{insights.topConverter.converted} conversions · {insights.topConverter.conversion_rate.toFixed(1)}% rate</p>
            </div>
          )}
          {insights.fastestMatch && (
            <div className="cn-card p-5" data-testid="ref-insight-fastest-match">
              <div className="flex items-center gap-2 text-cn-primary mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-wider font-bold">Fastest time-to-match</span>
              </div>
              <p className="font-semibold text-cn-text">{insights.fastestMatch.organization}</p>
              <p className="text-xs text-cn-text-muted mt-1">{fmtHours(insights.fastestMatch.avg_time_to_match_hours)} avg · {insights.fastestMatch.matched} matches</p>
            </div>
          )}
          {insights.coldest && (
            <div className="cn-card p-5 bg-amber-50/40 border-amber-200" data-testid="ref-insight-cold">
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <ArrowDown className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-wider font-bold">Needs attention</span>
              </div>
              <p className="font-semibold text-cn-text">{insights.coldest.organization}</p>
              <p className="text-xs text-cn-text-muted mt-1">{insights.coldest.match_rate.toFixed(1)}% match rate · {insights.coldest.total} referrals</p>
            </div>
          )}
        </div>
      )}

      {/* TABLE */}
      <div className="cn-card overflow-hidden" data-testid="ref-sources-table-card">
        <div className="p-5 border-b border-cn-border">
          <h3 className="font-display font-semibold text-base text-cn-text">Referring organizations ({data?.sources?.length || 0})</h3>
          <p className="text-xs text-cn-text-muted mt-0.5">Sorted by {SORT_OPTIONS.find((s) => s.value === sortBy)?.label.toLowerCase()}.</p>
        </div>
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>
        ) : !data?.sources?.length ? (
          <div className="p-10 text-center" data-testid="ref-sources-empty">
            <AlertTriangle className="w-6 h-6 text-cn-text-muted mx-auto mb-2" />
            <p className="text-sm text-cn-text-muted">No referrals in this window yet. Try a wider date range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ref-sources-table">
              <thead className="bg-cn-bone-50 border-b border-cn-border">
                <tr className="text-left text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                  <th className="px-5 py-3">Source</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-right">Matched</th>
                  <th className="px-3 py-3 text-right">Converted</th>
                  <th className="px-3 py-3 text-right">Match rate</th>
                  <th className="px-3 py-3 text-right">Conv. rate</th>
                  <th className="px-3 py-3 text-right">Avg TTM</th>
                  <th className="px-3 py-3 text-right">Planners</th>
                  <th className="px-3 py-3 text-right">Urgent</th>
                  <th className="px-3 py-3 text-right">QBR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cn-border">
                {data.sources.map((s) => (
                  <tr key={s.organization} className="hover:bg-cn-bone-50/40" data-testid={`ref-row-${s.organization.replace(/\s+/g, '-')}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-cn-text">{s.organization}</p>
                      <p className="text-[11px] text-cn-text-muted mt-0.5">
                        Last: {s.last_referral_at ? new Date(s.last_referral_at).toLocaleDateString() : '—'}
                        {Object.keys(s.care_type_mix || {}).length > 0 && (
                          <> · Top care-type: <strong>{Object.entries(s.care_type_mix).sort((a, b) => b[1] - a[1])[0][0]}</strong></>
                        )}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-right font-bold">{s.total}</td>
                    <td className="px-3 py-3 text-right">{s.matched}</td>
                    <td className="px-3 py-3 text-right">{s.converted}</td>
                    <td className="px-3 py-3 text-right"><RateCell value={s.match_rate} target={70} /></td>
                    <td className="px-3 py-3 text-right"><RateCell value={s.conversion_rate} target={45} /></td>
                    <td className="px-3 py-3 text-right">{fmtHours(s.avg_time_to_match_hours)}</td>
                    <td className="px-3 py-3 text-right">{s.distinct_planners}</td>
                    <td className="px-3 py-3 text-right">
                      {s.urgent_count > 0
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold">{s.urgent_count}</span>
                        : <span className="text-cn-text-muted">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => downloadQbr(s.organization)}
                        disabled={downloadingQbr !== null}
                        className="inline-flex items-center gap-1 text-xs text-cn-primary hover:text-cn-accent font-semibold disabled:opacity-50"
                        data-testid={`ref-qbr-${s.organization.replace(/\s+/g, '-')}`}
                      >
                        {downloadingQbr === s.organization.replace(/[^a-z0-9]/gi, '_').toLowerCase().replace(/^_+|_+$/g, '')
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <FileDown className="w-3.5 h-3.5" />}
                        QBR
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-cn-text-muted mt-4">
        TTM = average time from referral receipt to clinician match. Match rate = (matched + placed + closed) / total.
        Conversion rate = (placed + closed) / total. Goal benchmarks: 70% match rate, 45% conversion within 14 days.
      </p>

      {/* Iter 168 — INTAKE-SIDE scorecard (Pre-Admission Inbox).
          The existing tables above look at the MATCHING funnel
          (referrals → clinician placement). This section looks at
          the INTAKE funnel (packets landing in the Pre-Admission
          Intake Inbox → admissions). Together they give BD a
          full-funnel picture: which hospitals send packets, and
          which of those packets actually become admits. */}
      <IntakeScorecardSection window={window} getAuthHeader={getAuthHeader} />
    </DashboardShell>
  );
}

/**
 * Intake-side referral-source scorecard — hits
 * GET /api/intake/analytics/sources. Kept in this file rather than a
 * separate page so BD sees BOTH funnels side-by-side.
 */
function IntakeScorecardSection({ window, getAuthHeader }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/intake/analytics/sources`, {
        ...getAuthHeader(),
        params: { days: window, include_all_orgs: true, limit: 50 },
      });
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (e) {
      // Non-blocking — the primary scorecard already loaded
    } finally {
      setLoading(false);
    }
  }, [window, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const fmtPct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
  const fmtHours = (v) => {
    if (v == null) return '—';
    if (v < 1) return `${Math.round(v * 60)}m`;
    if (v < 48) return `${v.toFixed(1)}h`;
    return `${Math.round(v / 24)}d`;
  };
  const tone = (v, good, ok) => (v == null ? 'text-cn-text-muted'
    : v >= good ? 'text-emerald-600'
    : v >= ok ? 'text-amber-600'
    : 'text-red-600');

  return (
    <section className="mt-10" data-testid="intake-scorecard-section">
      <header className="mb-4 flex items-end justify-between">
        <div>
          <h2 className="text-xl font-semibold text-cn-text dark:text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-cn-primary" /> Intake Inbox — Pre-Admission Funnel
          </h2>
          <p className="text-xs text-cn-text-muted mt-1">
            Packets received by the Pre-Admission Intake Inbox, ranked by facility. Time-to-admit is measured from packet arrival to Accept.
          </p>
        </div>
      </header>

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiTile
          label="Packets received"
          value={summary.total_referrals ?? 0}
          icon={Network}
          sub={`${summary.distinct_facilities ?? 0} facilities`}
          testid="intake-kpi-total"
        />
        <KpiTile
          label="Accepted"
          value={summary.accepted ?? 0}
          icon={TrendingUp}
          sub={`${summary.rejected ?? 0} rejected`}
          testid="intake-kpi-accepted"
        />
        <KpiTile
          label="Intake conversion"
          value={fmtPct(summary.overall_conversion_rate)}
          icon={ArrowUp}
          sub={<span className={tone(summary.overall_conversion_rate, 0.6, 0.35)}>
            accepted ÷ decided
          </span>}
          testid="intake-kpi-conversion"
        />
        <KpiTile
          label="Time to admit"
          value={fmtHours(summary.avg_time_to_admit_hours)}
          icon={Clock}
          sub={<span className={tone(
            summary.avg_time_to_admit_hours != null ? (24 - summary.avg_time_to_admit_hours) : null,
            18, 0,
          )}>packet → accept</span>}
          testid="intake-kpi-ttar"
        />
      </div>

      <div className="cn-card overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-cn-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-cn-text-muted" data-testid="intake-scorecard-empty">
            No intake packets in this window yet. Upload one at <code>/admin/intake</code>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider bg-cn-bg-subtle border-b border-cn-border font-bold text-cn-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">Facility</th>
                  <th className="px-3 py-2 text-right">Packets</th>
                  <th className="px-3 py-2 text-right">Accepted</th>
                  <th className="px-3 py-2 text-right">Rejected</th>
                  <th className="px-3 py-2 text-right">In review</th>
                  <th className="px-3 py-2 text-right">Conversion</th>
                  <th className="px-3 py-2 text-right">Time to admit</th>
                  <th className="px-3 py-2 text-right">Last packet</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.facility}
                    className="border-b border-cn-border last:border-0 hover:bg-cn-bg-subtle/50 transition-colors"
                    data-testid={`intake-source-row-${r.facility.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <td className="px-3 py-3 font-semibold text-cn-text flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-cn-text-muted" />
                      {r.facility}
                    </td>
                    <td className="px-3 py-3 text-right font-bold">{r.total_referrals}</td>
                    <td className="px-3 py-3 text-right text-emerald-700">{r.accepted}</td>
                    <td className="px-3 py-3 text-right text-red-700">{r.rejected}</td>
                    <td className="px-3 py-3 text-right text-amber-700">{r.in_review}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${tone(r.conversion_rate, 0.6, 0.35)}`}>
                      {fmtPct(r.conversion_rate)}
                    </td>
                    <td className="px-3 py-3 text-right">{fmtHours(r.avg_time_to_admit_hours)}</td>
                    <td className="px-3 py-3 text-right text-cn-text-muted text-xs">
                      {r.last_referral_at ? new Date(r.last_referral_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-cn-text-muted mt-2">
        TTA = mean time from packet arrival to Accept. Conversion rate excludes still-in-review packets.
      </p>
    </section>
  );
}

function KpiTile({ label, value, icon: Icon, sub, testid }) {
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className="w-4 h-4 text-cn-primary" />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
      <div className="mt-1 text-xs text-cn-text-muted">{sub}</div>
    </div>
  );
}
